import React, { useState, useEffect } from "react";
import {
    Box,
    Text,
    Button,
    Input,
    VStack,
    Grid,
    GridItem,
    useToast,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Select,
    Spinner,
    InputGroup
} from "@chakra-ui/react";
import * as api from "../../api";
import { useAuth } from "../../context/Auth.context";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { forwardRef } from "react";

const Closure = () => {
    const [closures, setClosures] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [conflictingAppointments, setConflictingAppointments] = useState([]);
    const [conflictsChecked, setConflictsChecked] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [newDate, setNewDate] = useState("");
    const { user, isAuthed, ready } = useAuth();
    const [message, setMessage] = useState("");
    const navigate = useNavigate();
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState("");
    const [validationMessage, setValidationMessage] = useState("");
    const [loadingSlots, setLoadingSlots] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedClosure, setSelectedClosure] = useState(null);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const toast = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [closureDates, setClosureDates] = useState([]);
    const [fullyBookedDates, setFullyBookedDates] = useState([]);

    const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
        <InputGroup mb={4} width="100%">
            <Input
                onClick={onClick}
                ref={ref}
                value={value}
                placeholder="Selecteer een datum"
                readOnly
                cursor="pointer"
                width="100%"
                height="40px"
                fontSize="md"
                marginLeft="0px"
            />
        </InputGroup>
    ));

    const formatDateISO = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };


    useEffect(() => {
        if (ready && isAuthed && !user?.roles?.includes('admin')) {
            setMessage("Je moet admin rechten hebben om deze pagina te bekijken.");
            setTimeout(() => navigate("/login"), 2000);
        } else if (ready && !isAuthed) {
            setMessage("Je moet ingelogd zijn om deze pagina te bekijken.");
            setTimeout(() => navigate("/login"), 2000);
        }
    }, [ready, isAuthed, user, navigate]);

    useEffect(() => {
        if (ready && isAuthed && user?.roles?.includes('admin')) {
        api.get(`/closures`)
            .then((res) => {
                const dates = res.items.flatMap(({ start_date, end_date }) => {
                    const datesInRange = [];
                    let current = new Date(start_date);
                    const end = new Date(end_date);

                    while (current <= end) {
                        datesInRange.push(new Date(current));
                        current.setDate(current.getDate() + 1);
                    }

                    return datesInRange;
                });
                setClosureDates(dates);
            })
            .catch((err) => console.error("Error fetching closures", err));
        }
    }, [ready, isAuthed, user]);


    useEffect(() => {
        if (ready && isAuthed && user?.roles?.includes('admin')) {
        fetchClosures();
        }
    }, [ready, isAuthed, user]);

    const fetchClosures = async () => {
        try {
            const response = await api.get(`/closures`);
            setClosures(response.items);
        } catch (error) {
            console.error("Error fetching closures:", error);
        }
    };

    // Hulpfunctie: Neem aan dat appointment_datetime in "YYYY-MM-DD HH:mm:ss" staat en toon dit netjes
    const formatDateTime = (dateTimeString) => {
        // Vervang de spatie door een 'T' zodat het als ISO-string wordt geïnterpreteerd
        const isoString = dateTimeString.replace(" ", "T");
        const date = new Date(isoString);
        return date.toLocaleString("nl-BE", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Europe/Brussels"
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("nl-BE", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    const deleteClosure = async () => {
        if (!selectedClosure) return;

        try {
            await api.del(`/closures/${selectedClosure.closureId}`);
            toast({
                title: "Sluiting opgeheven",
                description: "Klanten kunnen opnieuw afspraken maken in deze periode.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            fetchClosures();
            onDeleteClose();
        } catch (error) {
            console.error("Error deleting closure:", error);
        }
    };

    const checkConflicts = async () => {
        setValidationMessage(""); // reset vorige fouten

        if (!startDate || !endDate) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start < today || end < today) {
            setValidationMessage("Start- en einddatum mogen niet in het verleden liggen.");
            return;
        }

        if (end < start) {
            setValidationMessage("De einddatum moet na de startdatum liggen.");
            return;
        }

        // Als validatie geslaagd is → haal conflicten op
        try {
            const response = await api.get(`/appointments/closure-conflicts`, {
                params: { startDate, endDate }
            });

            setConflictingAppointments(response.items);
            setConflictsChecked(true);

            if (response.items.length === 0) {
                toast({
                    title: "Geen conflicten gevonden",
                    description: "De sluiting kan direct worden ingesteld.",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error fetching conflicts:", error);
            toast({
                title: "Fout bij ophalen van conflicten",
                description: "Er is een fout opgetreden bij het ophalen van conflicterende afspraken.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };


    const scheduleClosure = async () => {
        if (conflictingAppointments.length > 0) {
            toast({
                title: "Kan sluiting niet instellen",
                description: "Er zijn nog afspraken in deze periode!",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        api
            .post(`/closures`, { startDate, endDate })
            .then(() => {
                toast({
                    title: "Sluiting ingesteld",
                    description: `De winkel is gesloten van ${startDate} tot ${endDate}.`,
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                fetchClosures();
                setStartDate("");
                setEndDate("");
                setConflictsChecked(false);
            })
            .catch((error) => console.error("Error setting closure:", error));
    };

    const loadAvailableTimeSlots = async (date) => {
        setLoadingSlots(true);
        try {
            const response = await api.get(`/appointments/available?date=${date}`);
            setTimeSlots(response.availableSlots);
        } catch (error) {
            console.error("Error fetching time slots:", error);
            setTimeSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    const moveAppointment = async () => {
        if (!selectedAppointment || !newDate || !selectedTime) return;

        const localDateTime = `${newDate} ${selectedTime}:00`;
        setIsSaving(true);

        try {
            await api.put(`/appointments/${selectedAppointment.appointmentId}`, {
                appointment_datetime: localDateTime,
            });

            toast({
                title: "Afspraak verplaatst",
                description: `Afspraak verplaatst naar ${localDateTime}.`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            checkConflicts(); // herlaad conflicten
            onClose();
        } catch (error) {
            if (error.response?.status === 409) {
                toast({
                    title: "Tijdslot bezet",
                    description: "Kies een ander tijdslot, dit is al ingenomen.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                console.error("Error updating appointment:", error);
            }
        } finally {
            setIsSaving(false);
        }
    };



    return (
        <Box maxW="800px" mx="auto" mt="50px" p={5} boxShadow="lg" borderRadius="md" bg="white">
            {!ready ? (
                <Box textAlign="center" mt="120px">
                    <Text fontSize="lg" color="gray.500">Aan het laden...</Text>
                </Box>
            ) : !isAuthed ? (
                <Box
                    maxW="500px"
                    mx="auto"
                    mt="120px"
                    p={5}
                    boxShadow="lg"
                    borderRadius="md"
                    bg="white"
                    textAlign="center"
                >
                    <Text color="red.500" fontSize="lg" fontWeight="bold">
                        {message}
                    </Text>
                </Box>
            ) : (
                <>
                    <Text fontSize="2xl" fontWeight="bold" mb={4}>Sluiting plannen</Text>

                    <VStack spacing={4} align="stretch">
                        <Text>Startdatum</Text>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />

                        <Text>Einddatum</Text>
                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

                        <Button colorScheme="blue" onClick={checkConflicts} isDisabled={!startDate || !endDate}>
                            Controleer conflicten
                        </Button>

                        {validationMessage && (
                            <Text color="red.500" fontSize="sm" textAlign="center">
                                {validationMessage}
                            </Text>
                        )}

                        {conflictsChecked && (
                            <Button colorScheme="red" onClick={scheduleClosure} isDisabled={conflictingAppointments.length > 0}>
                                Sluiting bevestigen
                            </Button>
                        )}
                    </VStack>

                    {/* ✅ Nieuw: Sluitingen weergeven */}
                    {closures.length > 0 && (
                        <Box mt={6}>
                            <Text fontSize="xl" fontWeight="bold" mb={3}>Bestaande sluitingen</Text>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                {closures.map((closure) => (
                                    <GridItem key={closure.closureId} p={4} bg="gray.100" boxShadow="lg" borderRadius="lg">
                                        <Text><b>Periode:</b> {formatDate(closure.start_date)} - {formatDate(closure.end_date)}</Text>
                                        <Button colorScheme="red" mt={2} onClick={() => { setSelectedClosure(closure); onDeleteOpen(); }}>
                                            Sluiting opheffen
                                        </Button>
                                    </GridItem>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* ✅ Nieuw: Bevestigingspopup voor verwijderen */}
                    <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Sluiting opheffen</ModalHeader>
                            <ModalBody>
                                <Text>Ben je zeker dat je deze sluiting wilt opheffen?</Text>
                            </ModalBody>
                            <ModalFooter justifyContent="center">
                                <Button colorScheme="red" onClick={deleteClosure}>Bevestigen</Button>
                                <Button onClick={onDeleteClose} ml={2}>Annuleren</Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {conflictingAppointments.length > 0 && (
                        <Box mt={6}>
                            <Text fontSize="xl" fontWeight="bold" color="red.500" mb={3}>
                                Er zijn afspraken in deze periode!
                            </Text>

                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                {conflictingAppointments.map((app) => (
                                    <GridItem key={app.appointmentId} p={4} bg="white" boxShadow="lg" borderRadius="lg">
                                        <Text><b>Naam:</b> {app.userName}</Text>
                                        <Text><b>Email:</b> {app.email}</Text>
                                        <Text><b>Telefoonnummer:</b> {app.phoneNumber}</Text>
                                        <Text><b>Tijdstip:</b> {formatDateTime(app.appointment_datetime)}</Text>
                                        <Text><b>Behandeling:</b> {app.treatmentName}</Text>
                                        <Button
                                            colorScheme="blue"
                                            mt={2}
                                            onClick={() => {
                                                setSelectedAppointment(app);
                                                setNewDate("");
                                                setSelectedTime("");
                                                setTimeSlots([]);
                                                onOpen();
                                            }}
                                        >
                                            Afspraak verplaatsen
                                        </Button>
                                    </GridItem>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    <Modal isOpen={isOpen} onClose={onClose}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Afspraak verplaatsen</ModalHeader>
                            <ModalBody>
                                <Text>Nieuwe datum:</Text>
                                <DatePicker
                                    selected={newDate ? new Date(newDate) : null}
                                    onChange={(date) => {
                                        if (!date) return;
                                        const formatted = formatDateISO(date);
                                        setNewDate(formatted);
                                        loadAvailableTimeSlots(formatted);
                                    }}
                                    dateFormat="yyyy-MM-dd"
                                    excludeDates={[...closureDates, ...fullyBookedDates]}
                                    customInput={<CustomDateInput />}
                                />

                                <Text mt={2}>Nieuw tijdstip:</Text>
                                {loadingSlots ? (
                                    <Spinner />
                                ) : (
                                    <Select value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} placeholder="Kies een tijdslot">
                                        {timeSlots.map((slot, index) => (
                                            <option key={index} value={slot}>{slot}</option>
                                        ))}
                                    </Select>
                                )}
                            </ModalBody>
                            <ModalFooter justifyContent="center">
                                {isSaving ? (
                                    <Spinner size="md" />
                                ) : (
                                    <>
                                        <Button onClick={moveAppointment} colorScheme="blue" minW="120px">
                                            Opslaan
                                        </Button>
                                        <Button onClick={onClose} ml={3} minW="120px">
                                            Annuleren
                                        </Button>
                                    </>
                                )}
                            </ModalFooter>

                        </ModalContent>
                    </Modal>
                </>
            )}
        </Box>
    );
};

export default Closure;
